import React from "react";
import { connect } from "react-redux";
import { Field, FormSection, change } from "redux-form";
import numeral from "numeral";
import * as FormUtils from "common/FormUtils";
import * as utils from "common/utils";
import * as validations from "common/validations";
import Modal from "components/Modal";
import Grid from "components/Grid";
import GridCell from "components/GridCell";
import InputPack from "components/InputPack";
import SelectPack from "components/SelectPack2";
import RadioGroupPack from "components/RadioGroupPack";
import CheckboxPack from "components/CheckboxPack";
import BlockSelector from "components/new/BlockSelector";
import * as _utils from "./_utils";
import DistributionApplications from "./DistributionApplications";
import DistributionRemaining from "./DistributionRemaining";
import Alert from "components/Alert";
import * as domain from "common/domain";

class DistributionModal extends React.Component {
    constructor(props) {
        super(props);
        const { initialValues: destination } = props;
        const state = this._initState(destination, destination.structureId);
        this.state = { ...state };
    }

    _initState = (destination, structureId) => {
        const { budget = {} } = destination;

        const structure = (budget.structures || []).find((s) => s.id === structureId);
        const enterprise = (budget.enterprises || []).find((e) => e.id === (structure || {}).enterpriseId);

        const isDairyGoat = enterprise ? enterprise.type === "DairyGoat" : false;

        const applications = (destination.applications || []).map((application) => {
            const total = Object.keys(application.months || {}).reduce((sum, month) => {
                sum += parseInt(application.months[month] || 0, 10);
                return sum;
            }, 0);

            return {
                ...application,
                enterpriseId: destination.enterpriseId,
                total: total,
            };
        });

        const totals = _utils.getSpecifiedFeedingTotals(applications);

        const showStructureUtilisation = ["Structure"].includes(destination.type) && (isDairyGoat === false || structure.type === "MilkingShed");
        const showStorageCondition = (["Storage", "SpecifiedBlocks", "AllBlocks", "Enterprise", "Structure"].includes(destination.type) && destination.feedSupplement.category !== "DirectFeeding") || (destination.type === "OffFarm" && destination.feedSupplement.silageStack);

        return {
            destination,
            showBlockTypes: ["SpecifiedBlocks", "AllBlocks"].includes(destination.type),
            showBlocks: ["SpecifiedBlocks"].includes(destination.type),
            showAnimals: ["Enterprise"].includes(destination.type),
            showUtilisation: ["SpecifiedBlocks", "AllBlocks", "Enterprise"].includes(destination.type) && isDairyGoat === false,
            showStruture: ["Structure"].includes(destination.type),
            showStructureUtilisation,
            showStorageCondition,
            applications,
            hasApplications: applications.length > 0,
            hasBlocks: destination.blockIds && destination.blockIds.length > 0,
            hasEnterprise: destination.enterpriseId,
            isAllBlocks: destination.type === "AllBlocks",
            specifyFeeding: destination.specifyFeeding,
            condition: destination.storageCondition,
            utilisation: showStructureUtilisation ? destination.structureUtilisation : destination.utilisation,
            totals,
        };
    };

    _validate = (destination) => {
        const errors = {};

        const showAutumnAmount = _utils.showAutumnCuts(destination.budget, destination.feedSupplement);
        if (showAutumnAmount) {
            errors.amount = FormUtils.validators.requiredIf(destination.amount, destination.amountAutumn, "Required if autumn/winter amount not specified");
            errors.amountAutumn = FormUtils.validators.requiredIf(destination.amountAutumn, destination.amount, "Required if spring/summer amount not specified");
        } else {
            errors.amount = FormUtils.validators.required(destination.amount);
        }

        return errors;
    };

    _structureChanged = (e) => {
        const structureId = e.target.value;
        const state = this._initState(this.state.destination, structureId);
        this.setState(state);
    };

    _storageConditionChanged = (e) => {
        this.setState({ condition: e.target.value });
    };

    _utilisationChanged = (e) => {
        this.setState({ utilisation: e.target.value });
    };

    _blockTypeChanged = (e) => {
        const isAllBlocks = e.target.value === "AllBlocks";

        const applications = isAllBlocks ? [{ months: {}, total: 0 }] : [];

        if (isAllBlocks) {
            this.props.change("modal-form", "blockIds", []);
            this.props.change("modal-form", "applications", applications);
        }

        this.props.change("modal-form", "specifyFeeding", false);

        this.setState({
            showBlocks: !isAllBlocks,
            specifyFeeding: false,
            applications,
            isAllBlocks,
        });
    };

    _blocksRequired = (selectedBlockIds) => {
        return selectedBlockIds && selectedBlockIds.length > 0 ? undefined : "Required";
    };

    _blocksChanged = (blockIds) => {
        const applications = this.state.applications.filter((a) => blockIds.includes(a.blockId));
        const newBlockIds = blockIds.filter((blockId) => !applications.some((a) => a.blockId === blockId));
        newBlockIds.forEach((blockId) => applications.push({ blockId, months: {}, total: 0 }));
        this.props.change("modal-form", "applications", applications);
        const totals = _utils.getSpecifiedFeedingTotals(applications);
        this.setState({
            applications: applications,
            totals: totals,
            hasApplications: applications.length > 0,
            specifiedFeedingError: this._specifiedFeedingMustEqual100Percent(totals),
        });
    };

    _enterpriseChanged = (e) => {
        const enterpriseId = e.target.value;

        const applications = this.state.applications.map((application) => {
            return {
                ...application,
                enterpriseId,
            };
        });

        if (applications.length === 0) {
            applications.push({ enterpriseId, months: {}, total: 0 });
        }

        this.setState({ applications, hasEnterprise: true });
    };

    _specifyFeedingChanged = (e, checked) => {
        this.setState({ specifyFeeding: checked });
    };

    _specifiedFeedingMustEqual100Percent = (totals) => {
        return this.state.specifyFeeding !== true || totals.total === 100 ? undefined : "Total distribution must equal 100%";
    };

    _specifiedFeedingApplicationsMusthaveValues = (applications) => {
        if (!applications) return;
        let result = undefined;
        applications.forEach((a) => {
            if (a.months) {
                const appTotal = Object.keys(a.months).reduce((total, month) => (total += a.months[month]), 0);
                if (appTotal === 0) {
                    result = "Each block must have percentage values";
                }
            }
        });
        return result;
    };

    _onMonthChange = (application, month) => (e) => {
        const amount = parseInt(e.target.value || 0, 10);
        if (!application.months) application.months = {};

        application.months[month] = amount;
        application.total = Object.keys(application.months).reduce((sum, month) => {
            sum += parseInt(application.months[month], 10);
            return sum;
        }, 0);

        const applications = this.state.applications.map((app) => {
            if (application.blockId && application.blockId === app.blockId) return application;

            if (application.enterpriseId && application.enterpriseId === app.enterpriseId) return application;

            return app;
        });

        const totals = _utils.getSpecifiedFeedingTotals(applications);
        const errorMessage = this._specifiedFeedingMustEqual100Percent(totals) || this._specifiedFeedingApplicationsMusthaveValues(applications);
        this.setState({ applications, totals, specifiedFeedingError: errorMessage });
    };

    _formatIntOnBlur = (field) => (e) => {
        setTimeout(() => this.props.change("modal-form", field, FormUtils.formatters.formatInt(e.target.value)));
    };

    _formatDecimalOnBlur = (field) => (e) => {
        setTimeout(() => this.props.change("modal-form", field, FormUtils.formatters.formatDecimal(1)(e.target.value)));
    };

    render() {
        const { initialValues: destination, refData, modal } = this.props;
        const _storageConditions = _utils.getDistributionStorageConditions(refData, destination.type);
        const _structures = _utils.getAvailableDistributionStructures(destination.budget, destination.feedSupplement, destination.id, refData);
        const _enterprises = _utils.getAvailableDistributionEnterprises(destination.budget, destination.feedSupplement, destination.id, refData);
        const _amountTypeMeta = _utils.getAmountTypeMeta(destination.feedSupplement.amountType);
        const _availableBlocks = _utils.getAvailableDistributionBlocks(destination.budget);
        const _destinationType = utils.valueToText(refData.supplementDestinationTypes, destination.type);
        const _blockTypes = _utils.getBlockTypes(refData);
        const _showSpecifyFeeding = this.state.hasApplications || this.state.hasBlocks || this.state.isAllBlocks || this.state.hasEnterprise;
        const _conditionTip = this.state.showStorageCondition && domain.SupplementStorageTips[this.state.condition];
        const _utilisationTip = this.state.showUtilisation ? domain.SupplementPaddockConditionTips[this.state.utilisation] : this.state.showStructureUtilisation && domain.SupplementStructureConditionTips[this.state.utilisation];
        const _showAutumnAmount = _utils.showAutumnCuts(destination.budget, destination.feedSupplement);

        let _maxAmount = numeral(destination.amount || 0)
            .add(destination.amounts.totalRemaining)
            .value();
        if (_showAutumnAmount) {
            _maxAmount = numeral(destination.amount || 0)
                .add(destination.amounts.springSummerRemaining)
                .value();
        }
        const _maxAutumnAmount = numeral(destination.amountAutumn || 0)
            .add(destination.amounts.autumnWinterRemaining)
            .value();

        const _onSubmit = (viewModel) => {
            const error = this.state.specifyFeeding ? this._specifiedFeedingMustEqual100Percent(this.state.totals) || this._specifiedFeedingApplicationsMusthaveValues(this.state.applications) : undefined;
            if (error) {
                this.setState({ specifiedFeedingError: error });
            } else {
                const { applications = [] } = this.state;
                const specifiedApplications = this.state.specifyFeeding
                    ? applications
                    : applications.map((application) => {
                          return {
                              ...application,
                              months: {},
                          };
                      });
                const destination = {
                    id: viewModel.id,
                    type: viewModel.type,
                    amount: numeral(viewModel.amount).value() || 0,
                    amountAutumn: _showAutumnAmount ? numeral(viewModel.amountAutumn).value() : 0,
                    storageCondition: this.state.showStorageCondition ? viewModel.storageCondition : null,
                    utilisation: viewModel.utilisation,
                    structureUtilisation: viewModel.structureUtilisation,
                    structureId: viewModel.structureId,
                    enterpriseId: viewModel.enterpriseId,
                    applications: specifiedApplications,
                    modalWasDirty: viewModel.modalWasDirty,
                };
                modal.onConfirm(destination);
            }
        };

        const allowDecimalAmounts = _amountTypeMeta.uom === "tonnes";

        return (
            <Modal title={`Supplement distribution - ${_destinationType}`} submitLabel="Done" initialValues={destination} onSubmit={_onSubmit} validate={this._validate}>
                <Grid>
                    <GridCell className="u-md-width2of3">
                        <Field name="id" type="hidden" component="input" />
                        <Grid title="Distribution details" instructions={_maxAmount > 0 || _maxAutumnAmount > 0 ? "Enter details of how much to distribute and where" : "There is not enough remaining feed to distribute. You will need to reduce other amounts distributed or remove this distribution."}>
                            {_maxAmount > 0 && (
                                <GridCell className="u-lg-width1of2">
                                    <Field name="amount" label={`${_showAutumnAmount ? "Spring/summer amount" : "Amount"} (${_amountTypeMeta.uom})`} requiredLabel={true} placeholder="0" type="text" uom={_amountTypeMeta.uom} onBlur={allowDecimalAmounts ? this._formatDecimalOnBlur("amount") : this._formatIntOnBlur("amount")} validate={[validations.required, validations.range(allowDecimalAmounts ? 0.1 : 1, _maxAmount)]} component={InputPack} />
                                </GridCell>
                            )}
                            {_showAutumnAmount && _maxAutumnAmount > 0 && (
                                <GridCell className="u-lg-width1of2">
                                    <Field name="amountAutumn" label="Autumn/winter amount (cuts)" requiredLabel={true} placeholder="0" type="text" uom="cuts" onBlur={this._formatIntOnBlur("amountAutumn")} validate={[validations.required, validations.range(1, _maxAutumnAmount)]} component={InputPack} />
                                </GridCell>
                            )}
                            {this.state.showStorageCondition && (
                                <GridCell className="u-lg-width1of2">
                                    <Field name="storageCondition" label="Storage condition" placeholder="Select an option" options={_storageConditions} onChange={this._storageConditionChanged} tip={_conditionTip ? _conditionTip.text : undefined} required validate={[validations.required]} component={SelectPack} />
                                </GridCell>
                            )}
                            {this.state.showUtilisation && (
                                <GridCell className="u-lg-width1of2">
                                    <Field name="utilisation" label="Utilisation" placeholder="Select an option" options={refData.supplementUtilisations} onChange={this._utilisationChanged} tip={_utilisationTip ? _utilisationTip.text : undefined} required={true} validate={[validations.required]} component={SelectPack} />
                                </GridCell>
                            )}
                            {this.state.showStruture && (
                                <GridCell className="u-lg-width1of2">
                                    <Field name="structureId" label="Structure" placeholder="Select a structure" options={_structures} onChange={this._structureChanged} required={true} validate={[validations.required]} component={SelectPack} />
                                </GridCell>
                            )}
                            {this.state.showStructureUtilisation && (
                                <GridCell className="u-lg-width1of2">
                                    <Field name="structureUtilisation" label="Utilisation" placeholder="Select an option" options={refData.supplementStructureUtilisations} onChange={this._utilisationChanged} tip={_utilisationTip ? _utilisationTip.text : undefined} required={true} validate={[validations.required]} component={SelectPack} />
                                </GridCell>
                            )}
                        </Grid>
                    </GridCell>
                    <GridCell className="u-md-width1of3">
                        <DistributionRemaining destination={destination} showAutumnAmount={_showAutumnAmount} amounts={destination.amounts} remainingAmount={destination.maxAmount} uom={_amountTypeMeta.uom} />
                    </GridCell>
                </Grid>
                {(this.state.showBlockTypes || this.state.showBlocks || this.state.showAnimals) && (
                    <Grid title="Distribute to">
                        {this.state.showBlockTypes && (
                            <GridCell>
                                <Field name="type" options={_blockTypes} onChange={this._blockTypeChanged} inline={true} validate={[validations.required]} component={RadioGroupPack} />
                            </GridCell>
                        )}
                        {this.state.showBlocks && (
                            <GridCell>
                                <Field name="blockIds" availableBlocks={_availableBlocks} onChange={(e, newValue) => this._blocksChanged(newValue)} validate={[validations.required]} component={BlockSelector} />
                            </GridCell>
                        )}
                        {this.state.showAnimals && (
                            <GridCell>
                                <Field name="enterpriseId" options={_enterprises} onChange={this._enterpriseChanged} inline={true} validate={[validations.required]} component={RadioGroupPack} />
                            </GridCell>
                        )}
                        {_showSpecifyFeeding && (
                            <GridCell>
                                <Field name="specifyFeeding" label="Specify feeding % by months (leave unchecked to use defaults)" onChange={this._specifyFeedingChanged} component={CheckboxPack} />
                            </GridCell>
                        )}
                        {this.state.specifyFeeding && (
                            <GridCell>
                                <div className="Table u-mt-md">
                                    <Alert type="help" text="Enter the percentage of the distributed amount fed by month. The total must equal 100%" />

                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Destination</th>
                                                <th className="u-textCenter">Jul</th>
                                                <th className="u-textCenter">Aug</th>
                                                <th className="u-textCenter">Sep</th>
                                                <th className="u-textCenter">Oct</th>
                                                <th className="u-textCenter">Nov</th>
                                                <th className="u-textCenter">Dec</th>
                                                <th className="u-textCenter">Jan</th>
                                                <th className="u-textCenter">Feb</th>
                                                <th className="u-textCenter">Mar</th>
                                                <th className="u-textCenter">Apr</th>
                                                <th className="u-textCenter">May</th>
                                                <th className="u-textCenter">Jun</th>
                                                <th className="th--shrink u-textCenter">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {this.state.applications.map((application, index) => (
                                                <FormSection key={index} name={`applications[${index}]`}>
                                                    <DistributionApplications isAllBlocks={this.state.isAllBlocks} application={application} budget={destination.budget} refData={refData} onMonthChange={this._onMonthChange} />
                                                </FormSection>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <th>Total</th>
                                                <th className={`u-textCenter ${this.state.totals.July > 100 ? "u-textError" : ""}`}>{this.state.totals.July} %</th>
                                                <th className={`u-textCenter ${this.state.totals.August > 100 ? "u-textError" : ""}`}>{this.state.totals.August} %</th>
                                                <th className={`u-textCenter ${this.state.totals.September > 100 ? "u-textError" : ""}`}>{this.state.totals.September} %</th>
                                                <th className={`u-textCenter ${this.state.totals.October > 100 ? "u-textError" : ""}`}>{this.state.totals.October} %</th>
                                                <th className={`u-textCenter ${this.state.totals.November > 100 ? "u-textError" : ""}`}>{this.state.totals.November} %</th>
                                                <th className={`u-textCenter ${this.state.totals.December > 100 ? "u-textError" : ""}`}>{this.state.totals.December} %</th>
                                                <th className={`u-textCenter ${this.state.totals.January > 100 ? "u-textError" : ""}`}>{this.state.totals.January} %</th>
                                                <th className={`u-textCenter ${this.state.totals.February > 100 ? "u-textError" : ""}`}>{this.state.totals.February} %</th>
                                                <th className={`u-textCenter ${this.state.totals.March > 100 ? "u-textError" : ""}`}>{this.state.totals.March} %</th>
                                                <th className={`u-textCenter ${this.state.totals.April > 100 ? "u-textError" : ""}`}>{this.state.totals.April} %</th>
                                                <th className={`u-textCenter ${this.state.totals.May > 100 ? "u-textError" : ""}`}>{this.state.totals.May} %</th>
                                                <th className={`u-textCenter ${this.state.totals.June > 100 ? "u-textError" : ""}`}>{this.state.totals.June} %</th>
                                                <th className="u-textCenter">
                                                    <h4 className={`u-mt-0 ${this.state.totals.total === 100 ? "u-textSuccess" : "u-textError"}`}>{this.state.totals.total} %</h4>
                                                </th>
                                            </tr>
                                        </tfoot>
                                    </table>
                                    {this.state.specifiedFeedingError && <div className="Field-error u-block">{this.state.specifiedFeedingError}</div>}
                                </div>
                            </GridCell>
                        )}
                    </Grid>
                )}
            </Modal>
        );
    }
}

export default connect(null, { change })(DistributionModal);
